import React, { useState } from 'react';
import { MapPinIcon, ThumbsUpIcon, MessageCircleIcon, UserPlusIcon, CompassIcon, GlobeIcon, ShareIcon, AnchorIcon, ClockIcon, ThermometerIcon, StarIcon, HeartIcon, DropletIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
type LocationPostProps = {
  id: string;
  user: {
    name: string;
    image: string;
    role?: string;
  };
  location: {
    name: string;
    distance: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    rating?: number;
    conditions?: string;
  };
  content: string;
  images?: string[];
  likes: number;
  comments: number;
  shares?: number;
  time: string;
  isEvent?: boolean;
  event?: {
    title: string;
    type: string;
    date: string;
    time: string;
    going: number;
  };
  diveDetails?: {
    depth: string;
    duration: string;
    temperature: string;
  };
};
export const LocationPost: React.FC<LocationPostProps> = ({
  id,
  user,
  location,
  content,
  images,
  likes,
  comments,
  shares = 0,
  time,
  isEvent,
  event,
  diveDetails
}) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(likes);
  const handleLike = () => {
    if (liked) {
      setLikeCount(likeCount - 1);
    } else {
      setLikeCount(likeCount + 1);
    }
    setLiked(!liked);
  };
  return <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 hover:shadow-md transition-shadow" data-id="element-77">
      <div className="flex items-start gap-3 mb-4" data-id="element-78">
        <img src={user.image} alt={user.name} className="w-10 h-10 rounded-full" data-id="element-79" />
        <div className="flex-1" data-id="element-80">
          <div className="flex items-center flex-wrap" data-id="element-81">
            <h3 className="font-medium" data-id="element-82">{user.name}</h3>
            {user.role && <Badge variant="secondary" className="ml-2 text-xs" data-id="element-83">
                {user.role}
              </Badge>}
            <Button variant="ghost" size="sm" className="ml-auto text-primary py-1 px-2.5 hover:bg-primary/5" data-id="element-84">
              <UserPlusIcon size={14} className="mr-1" data-id="element-85" />
              Follow
            </Button>
          </div>
          <p className="text-sm text-gray-500" data-id="element-86">{time}</p>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center text-sm text-gray-600 gap-2" data-id="element-87">
        <div className="flex items-center" data-id="element-88">
          <MapPinIcon size={14} className="mr-1 text-primary" data-id="element-89" />
          <span className="font-medium" data-id="element-90">{location.name}</span>
        </div>
        <div className="flex items-center" data-id="element-91">
          <CompassIcon size={14} className="mr-1 text-primary" data-id="element-92" />
          <span data-id="element-93">{location.distance} away</span>
        </div>
        {location.rating && <div className="flex items-center" data-id="element-94">
            <div className="flex" data-id="element-95">
              {[...Array(5)].map((_, i) => <StarIcon key={i} size={12} className={i < Math.floor(location.rating!) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'} data-id="element-96" />)}
            </div>
            <span className="ml-1" data-id="element-97">{location.rating}</span>
          </div>}
        {location.conditions && <div data-id="element-98">
            <Badge variant="success" className="text-xs" data-id="element-99">
              {location.conditions}
            </Badge>
          </div>}
      </div>
      {/* Dive details - enhanced display */}
      {diveDetails && <div className="flex flex-wrap gap-3 mb-4 bg-primary/5 p-3 rounded-lg border border-primary/10" data-id="element-100">
          <div className="flex items-center text-sm" data-id="element-101">
            <AnchorIcon size={14} className="mr-1.5 text-primary" data-id="element-102" />
            <span data-id="element-103">{diveDetails.depth}</span>
          </div>
          <div className="flex items-center text-sm" data-id="element-104">
            <ClockIcon size={14} className="mr-1.5 text-primary" data-id="element-105" />
            <span data-id="element-106">{diveDetails.duration}</span>
          </div>
          <div className="flex items-center text-sm" data-id="element-107">
            <ThermometerIcon size={14} className="mr-1.5 text-primary" data-id="element-108" />
            <span data-id="element-109">{diveDetails.temperature}</span>
          </div>
        </div>}
      <p className="mb-4" data-id="element-110">{content}</p>
      {images && images.length > 0 && <div className="relative mb-4" data-id="element-111">
          <div className="grid grid-cols-2 gap-2" data-id="element-112">
            {images.map((image, index) => <img key={index} src={image} alt={`Dive photo ${index + 1}`} className="w-full h-40 object-cover rounded-lg hover:opacity-95 transition-opacity cursor-pointer" data-id="element-113" />)}
          </div>
          <div className="absolute bottom-2 left-2 bg-white/80 backdrop-blur-sm py-1 px-2 rounded-lg flex items-center text-xs shadow-sm" data-id="element-114">
            <MapPinIcon size={12} className="mr-1 text-primary" data-id="element-115" />
            <span data-id="element-116">{location.name}</span>
          </div>
        </div>}
      {isEvent && event && <div className="bg-primary/5 rounded-lg p-4 mb-4 border border-primary/10" data-id="element-117">
          <div className="flex justify-between" data-id="element-118">
            <h3 className="font-medium" data-id="element-119">{event.title}</h3>
            <Badge variant="primary" data-id="element-120">{event.type}</Badge>
          </div>
          <p className="text-sm text-gray-600 flex items-center mt-1" data-id="element-121">
            <MapPinIcon size={14} className="mr-1" data-id="element-122" />
            {location.name} • {location.distance} away
          </p>
          <p className="text-sm text-gray-600 mt-1" data-id="element-123">
            {event.date} • {event.time}
          </p>
          <div className="flex items-center justify-between mt-3" data-id="element-124">
            <div className="flex items-center gap-1" data-id="element-125">
              <div className="flex -space-x-2" data-id="element-126">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs" data-id="element-127">
                  +{event.going}
                </div>
              </div>
              <span className="text-xs text-gray-500" data-id="element-128">going</span>
            </div>
            <Button variant="primary" size="sm" className="shadow-sm hover:shadow" data-id="element-129">
              RSVP
            </Button>
          </div>
        </div>}
      {/* Enhanced engagement section */}
      <div className="border-t border-gray-100 pt-3 mt-2" data-id="element-130">
        <div className="flex justify-between items-center" data-id="element-131">
          <div className="flex gap-4" data-id="element-132">
            <button className={`flex items-center gap-1.5 py-1 px-2 rounded-md transition-colors ${liked ? 'text-primary bg-primary/5' : 'text-gray-500 hover:bg-gray-50'}`} onClick={handleLike} data-id="element-133">
              {liked ? <HeartIcon size={16} className="fill-primary" data-id="element-134" /> : <ThumbsUpIcon size={16} data-id="element-135" />}
              <span className="text-sm" data-id="element-136">{likeCount}</span>
            </button>
            <button className="flex items-center gap-1.5 py-1 px-2 rounded-md text-gray-500 hover:bg-gray-50 transition-colors" data-id="element-137">
              <MessageCircleIcon size={16} data-id="element-138" />
              <span className="text-sm" data-id="element-139">{comments}</span>
            </button>
            <button className="flex items-center gap-1.5 py-1 px-2 rounded-md text-gray-500 hover:bg-gray-50 transition-colors" data-id="element-140">
              <ShareIcon size={16} data-id="element-141" />
              <span className="text-sm" data-id="element-142">{shares}</span>
            </button>
          </div>
          <div className="flex items-center gap-1 text-gray-500 bg-gray-50 py-1 px-2 rounded-md" data-id="element-143">
            <GlobeIcon size={14} data-id="element-144" />
            <span className="text-xs" data-id="element-145">{location.name}</span>
          </div>
        </div>
      </div>
      {/* Quick comment input */}
      <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100" data-id="element-146">
        <img src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80" alt="Your profile" className="w-8 h-8 rounded-full" data-id="element-147" />
        <div className="flex-1 bg-gray-50 rounded-full px-4 py-1.5 text-sm text-gray-500 hover:bg-gray-100 transition-colors cursor-pointer" data-id="element-148">
          Add a comment...
        </div>
      </div>
      {/* Mini map preview */}
      <div className="mt-3 border-t pt-3" data-id="element-149">
        <div className="h-20 bg-gray-100 rounded-lg overflow-hidden relative cursor-pointer hover:opacity-90 transition-opacity" data-id="element-150">
          <div className="absolute inset-0 opacity-70 bg-[url('https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/static/pin-s+0066CC(${location.coordinates.lng},${location.coordinates.lat})/${location.coordinates.lng},${location.coordinates.lat},11,0/300x100?access_token=pk.dummy')]" data-id="element-151"></div>
          <div className="absolute bottom-1 right-1 bg-white/90 backdrop-blur-sm py-1 px-2 rounded-md text-xs flex items-center shadow-sm" data-id="element-152">
            <MapPinIcon size={10} className="mr-1 text-primary" data-id="element-153" />
            <span data-id="element-154">View on map</span>
          </div>
        </div>
      </div>
    </div>;
};